/**
 * КАРТКА НОМЕНКЛАТУРИ — окрема сторінка (ч.1059, рішення власника: картки
 * довідників — звичайні сторінки зі своєю адресою + чернетка незбережених
 * правок). Маршрути: /nomenclature/new і /nomenclature/:id.
 *
 * Раніше картка була панеллю (Drawer) поверх списку без власної адреси:
 * «назад», оновлення сторінки чи перехід в інший розділ її закривали, а
 * незбережене губилось.
 */
import { useCallback, useMemo, useState } from 'react';
import SearchSelect from '@/components/SearchSelect';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { nomenclature, type Nomenclature, type NomenclatureCreate } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import ImportedBadge from '@/shared/components/ImportedBadge';
import AccountSelect from '@/components/AccountSelect';
import { NomenclaturePricesTab } from './NomenclaturePricesTab';
import { NomenclatureKitTab } from './NomenclatureKitTab';
import { NomenclatureSeriesTab } from './NomenclatureSeriesTab';
import { NomenclatureAccountPreview } from './NomenclatureAccountPreview';
import RequiredLabel from '@/components/RequiredLabel';
import UktzedPicker from '@/components/UktzedPicker';
import DkppPicker from '@/components/DkppPicker';
import { useConfirm } from '@/components/ConfirmProvider';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { invalidateRefLists } from '@/shared/utils/refLists';
import {
  DraftSlotsProvider, confirmPendingRows, withLegacyForm, type DraftSlots,
} from '@/shared/hooks/draftSlots';

const EMPTY: NomenclatureCreate = {
  code: null,
  article: null,
  name: '',
  full_name: null,
  is_service: false,
  is_excise: false,
  ukt_zed_code: null,
  country_origin_code: null,
  base_uom_id: null,
  vat_rate_id: null,
  default_cost_item_id: null,
  kind_id: null,
  account_id: null,
  transfer_account_id: null,
  off_balance_account_id: null,
  off_balance_transfer_account_id: null,
  tax_purpose_id: null,
  income_cost_tax_purpose_id: null,
};

type CardTab = 'main' | 'accounts' | 'prices' | 'kit' | 'series';

/** Збережена позиція → стан форми картки. */
function toForm(n: Nomenclature): NomenclatureCreate {
  return {
    code: n.code, article: n.article, name: n.name, full_name: n.full_name,
    is_service: n.is_service, is_excise: n.is_excise,
    use_series: !!n.use_series,
    ukt_zed_code: n.ukt_zed_code, country_origin_code: n.country_origin_code,
    base_uom_id: n.base_uom_id,
    vat_rate_id: n.vat_rate_id, default_cost_item_id: n.default_cost_item_id,
    kind_id: n.kind_id,
    account_id: n.account_id, transfer_account_id: n.transfer_account_id,
    off_balance_account_id: n.off_balance_account_id,
    off_balance_transfer_account_id: n.off_balance_transfer_account_id,
    tax_purpose_id: n.tax_purpose_id,
    income_cost_tax_purpose_id: n.income_cost_tax_purpose_id,
  };
}

function errorText(e: unknown): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (x as { msg?: string }).msg ?? '').filter(Boolean).join('; ');
  }
  return e instanceof Error ? e.message : 'Не вдалося зберегти';
}

export default function NomenclatureCardPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();

  const itemQ = useQuery({
    queryKey: ['nomenclature-item', id],
    queryFn: () => nomenclature.get(id!),
    enabled: !isNew,
  });
  const item = itemQ.data ?? null;
  const [form, setForm] = useState<NomenclatureCreate>(EMPTY);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  // Заповнити форму ПІСЛЯ завантаження (раз на id).
  if (item && loadedId !== item.id) {
    setLoadedId(item.id);
    setForm(toForm(item));
  }
  // 2026-08-12: ефективний рахунок з превью → живий placeholder поля картки
  // (було статичне «281», що збивало для Матеріалів=201).
  const [resolvedPreview, setResolvedPreview] = useState<{ code: string; src: string } | null>(null);
  const [tab, setTab] = useState<CardTab>('main');

  // ч.1059: чернетка незбережених правок (як у документах). Вкладки «Ціни»,
  // «Комплектація», «Серії» зберігають свої рядки самі — картку вони не
  // зберігають, тому чернетку стираємо лише явним markSaved.
  // ч.1060: + слоти вкладок (ціна / склад набору / серія, що редагуються) —
  // не губляться при перемиканні вкладок і входять у чернетку картки.
  const [slots, setSlots] = useState<DraftSlots>({});
  const draftValue = useMemo(() => ({ form, slots }), [form, slots]);
  const restoreDraft = useCallback((raw: unknown) => {
    // Чернетка ч.1059 зберігала лише форму — розбираємо обидва формати.
    const v = withLegacyForm<{ form: NomenclatureCreate; slots?: DraftSlots }>(raw);
    setForm({ ...EMPTY, ...v.form });
    setSlots(v.slots ?? {});
  }, []);
  const draft = useFormDraft(draftValue, restoreDraft, {
    ready: isNew || (!!item && loadedId === item.id),
    subject: 'цієї картки',
    clearOnAnyMutation: false,
  });

  // 2026-06-02: UOM classifier (для базової одиниці виміру).
  const uomsList = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });
  const uomItems = useMemo(() => (uomsList.data ?? []).map((u) => ({
    id: u.id, label: `${u.short_name} — ${u.name}`, hint: u.kspovo_code,
  })), [uomsList.data]);
  // Бух-реквізити: ставка ПДВ + стаття затрат (1С УТП).
  const vatRatesList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const costItemsList = useQuery({ queryKey: ['cost-items'], queryFn: refs.costItems });
  // Види номенклатури (tenant-scoped). 2026-06-06.
  const kindsList = useQuery({ queryKey: ['nomenclature-kinds'], queryFn: refs.nomenclatureKinds.list });
  const taxPurposesQ = useQuery({ queryKey: ['tax-purposes'], queryFn: refs.taxPurposes });
  const kindById = useMemo(
    () => new Map((kindsList.data ?? []).map((k) => [k.id, k])),
    [kindsList.data],
  );

  const afterSave = () => {
    draft.markSaved();
    qc.invalidateQueries({ queryKey: ['nomenclature-item', id] });
    invalidateRefLists(qc, 'nomenclature');   // ч.1267: списки у відкритих документах
    navigate('/nomenclature');
  };
  const create = useMutation({
    mutationFn: (p: NomenclatureCreate) => nomenclature.create(p),
    onSuccess: afterSave,
  });
  const update = useMutation({
    mutationFn: (p: Partial<NomenclatureCreate>) => nomenclature.update(id!, p),
    onSuccess: afterSave,
  });
  const remove = useMutation({
    mutationFn: () => nomenclature.remove(id!),
    onSuccess: () => { draft.markSaved(); navigate('/nomenclature'); },
  });
  const save = async () => {
    // ч.1060: незбережені ціна / склад набору / серія у вкладках — питаємо,
    // щоб не загубились мовчки: після збереження картка закривається.
    if (!(await confirmPendingRows(slots, confirm))) return;
    if (isNew) create.mutate(form);
    else update.mutate(form);
  };
  const saveBlocker = !form.name.trim()
    ? 'Вкажіть назву номенклатури'
    : !form.kind_id
      ? 'Оберіть вид номенклатури — від нього залежать тип (товар/послуга) і рахунок обліку'
      : null;
  const saveError = create.error ?? update.error ?? remove.error;

  if (!isNew && itemQ.isLoading) {
    return <div className="card"><p>Завантаження…</p></div>;
  }
  if (!isNew && itemQ.isError) {
    return (
      <div className="card">
        <p>Номенклатуру не знайдено.</p>
        <button onClick={() => navigate('/nomenclature')}>до списку</button>
      </div>
    );
  }

  return (
    <DraftSlotsProvider slots={slots} setSlots={setSlots}>
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          {isNew ? t('nom.new') : (item?.name ?? '')}
          {item && <ImportedBadge source={item.external_source} />}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/nomenclature')}
          onSave={save}
          saveDisabled={!!saveBlocker || create.isPending || update.isPending}
          saveTitle={saveBlocker ?? undefined}
          savePending={create.isPending || update.isPending}
          postable={false}
          isNew={isNew}
          onDelete={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }); }}
        />
      </div>
      {saveError && (
        <div style={{
          background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)',
          color: 'var(--badge-red-fg)', padding: '0.6rem 0.75rem', borderRadius: 6,
          marginBottom: '0.75rem', fontSize: '0.9em',
        }}>
          Не вдалося зберегти: {errorText(saveError)}
        </div>
      )}

      {/* Закладки: Основне | Ціни (Ціни — лише для збереженої номенклатури). */}
      <div className="seg-tabs">
        <button className={tab === 'main' ? 'active' : ''} onClick={() => setTab('main')}>
          Основне
        </button>
        <button className={tab === 'accounts' ? 'active' : ''} onClick={() => setTab('accounts')}>
          Рахунки та податки
        </button>
        <button className={tab === 'prices' ? 'active' : ''}
          disabled={isNew}
          title={isNew ? 'Спершу збережіть номенклатуру' : undefined}
          onClick={() => setTab('prices')}>
          Ціни
        </button>
        <button className={tab === 'kit' ? 'active' : ''}
          disabled={isNew}
          title={isNew ? 'Спершу збережіть номенклатуру' : undefined}
          onClick={() => setTab('kit')}>
          Комплектація
        </button>
        <button className={tab === 'series' ? 'active' : ''}
          disabled={isNew}
          title={isNew ? 'Спершу збережіть номенклатуру' : undefined}
          onClick={() => setTab('series')}>
          Серії/Характеристики
        </button>
      </div>

      {tab === 'prices' && !isNew && id && (
        <NomenclaturePricesTab nomenclatureId={id} />
      )}

      {tab === 'kit' && !isNew && id && (
        <NomenclatureKitTab nomenclatureId={id} />
      )}

      {tab === 'series' && !isNew && id && (
        <NomenclatureSeriesTab nomenclatureId={id} />
      )}

      {tab === 'accounts' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Read-only показ ефективного рахунку з матриці (1С-міграція кладе
              рахунки сюди, не у плоске поле картки). Лише для збереженої
              номенклатури (потрібен її id). */}
          {!isNew && id && (
            <NomenclatureAccountPreview nomenclatureId={id}
              onResolved={(code, src) => setResolvedPreview(code ? { code, src } : null)} />
          )}
          {isNew && (
            <p style={{
              margin: 0, padding: '0.6rem 0.75rem', borderRadius: 6,
              background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)',
              color: 'var(--badge-blue-fg)', fontSize: '0.85em',
            }}>
              Рахунки вказувати не обов'язково: оберіть <b>вид номенклатури</b>
              {' '}на першій вкладці — рахунок обліку підбереться правилом виду
              (Товар → 281, Матеріали → 201, Запчастини → 207…).
              Після збереження тут з'явиться ефективний рахунок.
            </p>
          )}
          <p style={{ color: 'var(--muted)', margin: 0, fontSize: '0.85em' }}>
            Поля нижче — <b>override картки</b> для простих випадків. Спрацюють
            лише якщо для юрособи немає точнішого правила (за видом / складом).
            Правила — Довідники → Рахунки обліку (правила).
          </p>
          <label>Рахунок обліку
            <AccountSelect value={form.account_id ?? ''}
              onChange={(id) => setForm({ ...form, account_id: id || null })}
              placeholder={resolvedPreview
                ? `— авто: ${resolvedPreview.code} (${resolvedPreview.src}) —`
                : '— авто (за правилом виду) —'} />
          </label>
          <label>Рахунок передачі
            <AccountSelect value={form.transfer_account_id ?? ''}
              onChange={(id) => setForm({ ...form, transfer_account_id: id || null })}
              placeholder="— не задано (типово не потрібен) —" />
            <small style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>
              Для передачі у переробку/на комісію. У проводках поки не
              задіяний — заповнювати не обовʼязково (імпортовані точкові
              правила передачі зберігаються у матриці правил).
            </small>
          </label>
          <label>Забалансовий рахунок обліку
            <AccountSelect value={form.off_balance_account_id ?? ''} offBalance
              onChange={(id) => setForm({ ...form, off_balance_account_id: id || null })}
              placeholder="— не задано —" />
          </label>
          <label>Забалансовий рахунок передачі
            <AccountSelect value={form.off_balance_transfer_account_id ?? ''} offBalance
              onChange={(id) => setForm({ ...form, off_balance_transfer_account_id: id || null })}
              placeholder="— не задано —" />
          </label>
          <label>Податкове призначення (ПДВ)
            <select value={form.tax_purpose_id ?? ''}
              onChange={(e) => setForm({ ...form, tax_purpose_id: e.target.value || null })}>
              <option value="">— не задано —</option>
              {(taxPurposesQ.data ?? []).map((tp) => (
                <option key={tp.id} value={tp.id}>{tp.name_uk}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      {tab === 'main' && (
      <>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>
            {t('nom.code')}{' '}
            <small style={{ color: 'var(--muted)' }}>
              (auto: NOM-1, якщо порожнє)
            </small>
          </label>
          <input
            value={form.code ?? ''}
            onChange={(e) => setForm({ ...form, code: e.target.value || null })}
            placeholder="NOM-1 (auto)"
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>{t('nom.article')}</label>
          <input value={form.article ?? ''} onChange={(e) => setForm({ ...form, article: e.target.value || null })} />
        </div>
      </div>
      <div className="field">
        <RequiredLabel>{t('nom.name')}</RequiredLabel>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      {/* 2026-07-31 (рішення власника): поле «Повне найменування» прибрано
          з картки — достатньо одного найменування. Колонка full_name у БД
          лишається (імпорт 1С пише її; друк/списки читають name). */}
      {/* Вид номенклатури (2026-06-06): обрав вид → тип (is_service)
          визначається автоматично з виду. */}
      <div className="field">
        <RequiredLabel>Вид номенклатури</RequiredLabel>
        <select
          value={form.kind_id ?? ''}
          onChange={(e) => {
            const kid = e.target.value || null;
            const kind = kid ? kindById.get(kid) : null;
            setForm({
              ...form,
              kind_id: kid,
              // Тип авто з виду (юзер не редагує тип напряму).
              is_service: kind ? kind.is_service : form.is_service,
            });
          }}
        >
          <option value="">— не задано —</option>
          {(kindsList.data ?? []).map((k) => (
            <option key={k.id} value={k.id}>{k.name}</option>
          ))}
        </select>
        <small style={{ color: 'var(--muted)' }}>
          Тип: <strong>{form.is_service ? 'Послуга' : 'Товар'}</strong>
          {' '}(визначається з виду)
        </small>
      </div>
      <div style={{ display: 'flex', gap: '1rem' }}>
        <label><input type="checkbox" checked={form.is_excise} onChange={(e) => setForm({ ...form, is_excise: e.target.checked })} style={{ width: 'auto', marginRight: '0.4rem' }} />{t('nom.is_excise')}</label>
        <label title="Учёт серий: при отгрузке обязательно указывать серию"><input type="checkbox" checked={!!form.use_series} onChange={(e) => setForm({ ...form, use_series: e.target.checked })} style={{ width: 'auto', marginRight: '0.4rem' }} />Облік серій</label>
      </div>
      <div className="doc-grid" style={{ marginTop: '0.5rem' }}>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Одиниця виміру</label>
          {/* ч.1410: увесь ДК 011-96 — пошуком, часто вживані першими. */}
          <SearchSelect
            items={uomItems}
            value={form.base_uom_id ?? ''}
            onChange={(id) => setForm({ ...form, base_uom_id: id || null })}
            placeholder="— почніть вводити: пог.м, шт, кг —"
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>{t('nom.country')}</label>
          <input value={form.country_origin_code ?? ''} onChange={(e) => setForm({ ...form, country_origin_code: e.target.value || null })} maxLength={3} />
        </div>
      </div>
      {/* Бух-реквізити: ставка ПДВ + стаття затрат (1С УТП). */}
      <div className="doc-grid" style={{ marginTop: '0.5rem' }}>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Ставка ПДВ</label>
          <select
            value={form.vat_rate_id ?? ''}
            onChange={(e) => setForm({ ...form, vat_rate_id: e.target.value || null })}
          >
            <option value="">— Не задано —</option>
            {vatRatesList.data?.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Стаття витрат</label>
          <select
            value={form.default_cost_item_id ?? ''}
            onChange={(e) => setForm({ ...form, default_cost_item_id: e.target.value || null })}
          >
            <option value="">— Не задано —</option>
            {costItemsList.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ marginTop: '0.5rem' }}>
        <div className="field">
          {/* «Код для ПН»: одне поле ukt_zed_code, але джерело різне —
              товар → УКТЗЕД (picker з пошуком по довіднику),
              послуга → ДКПП (текстовий ввід; довідника ДКПП ще немає,
              TD-NOM-3). Так фронт не плутає коди послуг з УКТЗЕД-пошуком. */}
          {form.is_service ? (
            <>
              <label>Код ДКПП (послуги)</label>
              <DkppPicker
                value={form.ukt_zed_code ?? null}
                onChange={(code) => setForm({ ...form, ukt_zed_code: code })}
              />
            </>
          ) : (
            <>
              <label>{t('nom.ukt_zed')}</label>
              <UktzedPicker
                value={form.ukt_zed_code ?? null}
                onChange={(code) => setForm({ ...form, ukt_zed_code: code })}
              />
            </>
          )}
        </div>
      </div>
      </>
      )}
    </div>
    </DraftSlotsProvider>
  );
}
